import type { Dispatch, SetStateAction } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type {
  Project,
  PubmedTextQueryTemplate,
  TriageTemplateScope,
  UserTriageTemplateListItem,
} from '@pubmed-seq-reader/shared/schemas';
import type { AppSettings } from '@/lib/settings';

export type AiTemplateBackupReportEntry = {
  status: 'imported' | 'skippedDuplicate' | 'skippedMissingProject' | 'failed';
  templateId: string | null;
  templateName: string;
  projectId: string;
  reason?: string;
};

export type AiTemplateBackupReport = {
  fileName: string;
  startedAt: string;
  finishedAt: string;
  total: number;
  imported: number;
  skippedDuplicate: number;
  skippedMissingProject: number;
  failed: number;
  entries: AiTemplateBackupReportEntry[];
};

type SettingsPanel = 'profile' | 'pubmedTemplates' | 'aiTemplates' | 'aiTemplateBackup' | 'ocr' | 'ncbi' | 'templates';
const AI_BACKUP_IMPORT_TARGET_AUTO = '__auto__';

export type ProjectHubSettingsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  sessionEmail: string;
  projects: Project[];
  settingsPanel: SettingsPanel;
  setSettingsPanel: Dispatch<SetStateAction<SettingsPanel>>;
  settings: AppSettings;
  setSettings: Dispatch<SetStateAction<AppSettings>>;
  profileName: string;
  setProfileName: Dispatch<SetStateAction<string>>;
  profileSaving: boolean;
  handleSaveProfile: () => void | Promise<void>;
  currentPassword: string;
  setCurrentPassword: Dispatch<SetStateAction<string>>;
  newPassword: string;
  setNewPassword: Dispatch<SetStateAction<string>>;
  confirmNewPassword: string;
  setConfirmNewPassword: Dispatch<SetStateAction<string>>;
  passwordChanging: boolean;
  handleChangePassword: () => void | Promise<void>;
  aiTemplates: UserTriageTemplateListItem[];
  aiTemplateKeyword: string;
  setAiTemplateKeyword: Dispatch<SetStateAction<string>>;
  aiTemplateProjectFilter: string;
  setAiTemplateProjectFilter: Dispatch<SetStateAction<string>>;
  aiTemplateScopeFilter: 'all' | TriageTemplateScope;
  setAiTemplateScopeFilter: Dispatch<SetStateAction<'all' | TriageTemplateScope>>;
  aiTemplatesLoading: boolean;
  formatTemplateScopeLabel: (scope: TriageTemplateScope) => string;
  handleOpenEditAiTemplate: (template: UserTriageTemplateListItem) => void | Promise<void>;
  handleOpenTemplateProject: (template: UserTriageTemplateListItem) => void;
  handleRequestDeleteAiTemplate: (template: UserTriageTemplateListItem) => void;
  aiTemplateDeletingId: string | null;
  aiTemplateLimitReached: boolean;
  aiBackupExporting: boolean;
  aiBackupExportProgress: string;
  aiBackupImporting: boolean;
  aiBackupImportFileName: string;
  aiBackupImportTargetProjectId: string;
  setAiBackupImportTargetProjectId: Dispatch<SetStateAction<string>>;
  aiBackupLastReport: AiTemplateBackupReport | null;
  handleExportAiTemplateBackup: () => void | Promise<void>;
  handleSelectAiBackupImportFile: (file: File | null) => void;
  handleImportAiTemplateBackup: () => void | Promise<void>;
  queryTemplates: PubmedTextQueryTemplate[];
  newTemplateName: string;
  setNewTemplateName: (value: string) => void;
  newTemplateQuery: string;
  setNewTemplateQuery: (value: string) => void;
  handleCreateQueryTemplate: () => void | Promise<void>;
  queryTemplatesSaving: boolean;
  queryTemplatesLoading: boolean;
  editingQueryTemplateId: string | null;
  queryTemplatesDeletingId: string | null;
  editingQueryTemplateName: string;
  setEditingQueryTemplateName: (value: string) => void;
  editingQueryTemplateQuery: string;
  setEditingQueryTemplateQuery: (value: string) => void;
  handleCancelEditQueryTemplate: () => void;
  handleSaveEditQueryTemplate: () => void | Promise<void>;
  handleStartEditQueryTemplate: (template: PubmedTextQueryTemplate) => void;
  handleDeleteQueryTemplate: (template: PubmedTextQueryTemplate) => void | Promise<void>;
  handleAddOcrApiKey: () => void;
  handleChangeOcrApiKey: (index: number, value: string) => void;
  handleRemoveOcrApiKey: (index: number) => void;
  loadQueryTemplates: () => Promise<void>;
};

export function ProjectHubSettingsDialog(props: ProjectHubSettingsDialogProps) {
  const { t } = useTranslation();
  const {
    open,
    onOpenChange,
    onClose,
    sessionEmail,
    projects,
    settingsPanel,
    setSettingsPanel,
    settings,
    setSettings,
    profileName,
    setProfileName,
    profileSaving,
    handleSaveProfile,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmNewPassword,
    setConfirmNewPassword,
    passwordChanging,
    handleChangePassword,
    aiTemplates,
    aiTemplateKeyword,
    setAiTemplateKeyword,
    aiTemplateProjectFilter,
    setAiTemplateProjectFilter,
    aiTemplateScopeFilter,
    setAiTemplateScopeFilter,
    aiTemplatesLoading,
    formatTemplateScopeLabel,
    handleOpenEditAiTemplate,
    handleOpenTemplateProject,
    handleRequestDeleteAiTemplate,
    aiTemplateDeletingId,
    aiTemplateLimitReached,
    aiBackupExporting,
    aiBackupExportProgress,
    aiBackupImporting,
    aiBackupImportFileName,
    aiBackupImportTargetProjectId,
    setAiBackupImportTargetProjectId,
    aiBackupLastReport,
    handleExportAiTemplateBackup,
    handleSelectAiBackupImportFile,
    handleImportAiTemplateBackup,
    queryTemplates,
    newTemplateName,
    setNewTemplateName,
    newTemplateQuery,
    setNewTemplateQuery,
    handleCreateQueryTemplate,
    queryTemplatesSaving,
    queryTemplatesLoading,
    editingQueryTemplateId,
    queryTemplatesDeletingId,
    editingQueryTemplateName,
    setEditingQueryTemplateName,
    editingQueryTemplateQuery,
    setEditingQueryTemplateQuery,
    handleCancelEditQueryTemplate,
    handleSaveEditQueryTemplate,
    handleStartEditQueryTemplate,
    handleDeleteQueryTemplate,
    handleAddOcrApiKey,
    handleChangeOcrApiKey,
    handleRemoveOcrApiKey,
    loadQueryTemplates,
  } = props;

  const formatBackupStatus = (status: AiTemplateBackupReportEntry['status']) => {
    if (status === 'imported') return t('projectHub.settings.aiBackup.report.status.imported');
    if (status === 'skippedDuplicate') return t('projectHub.settings.aiBackup.report.status.skippedDuplicate');
    if (status === 'skippedMissingProject') return t('projectHub.settings.aiBackup.report.status.skippedMissingProject');
    return t('projectHub.settings.aiBackup.report.status.failed');
  };

  return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="h-[92vh] overflow-hidden border-0 bg-transparent p-0 shadow-none sm:max-w-[860px] !flex !flex-col">
          <DialogTitle className="sr-only">{t('projectHub.settings.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('projectHub.settings.description')}
          </DialogDescription>
          <Card className="flex min-h-0 flex-1 flex-col rounded-2xl border-slate-200 bg-white shadow-[0_32px_80px_-30px_rgba(15,23,42,0.5)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl font-semibold text-slate-900">{t('projectHub.settings.title')}</CardTitle>
            </CardHeader>

            <CardContent className="flex-1 min-h-0 overflow-hidden pt-1">
              <div className="grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)] gap-4 md:grid-cols-[180px_minmax(0,1fr)] md:grid-rows-[minmax(0,1fr)]">
                <div className="space-y-2 border-b border-slate-100 pb-3 md:border-b-0 md:border-r md:pb-0 md:pr-3">
                  <Button
                    type="button"
                    variant={settingsPanel === 'profile' ? 'default' : 'ghost'}
                    className="w-full justify-start rounded-lg"
                    onClick={() => setSettingsPanel('profile')}
                  >
                    {t('projectHub.settings.tabs.profile')}
                  </Button>
                  <Button
                    type="button"
                    variant={settingsPanel === 'pubmedTemplates' || settingsPanel === 'templates' ? 'default' : 'ghost'}
                    className="w-full justify-start rounded-lg"
                    onClick={() => setSettingsPanel('pubmedTemplates')}
                  >
                    {t('projectHub.settings.tabs.pubmedTemplates')}
                  </Button>
                  <Button
                    type="button"
                    variant={settingsPanel === 'aiTemplates' ? 'default' : 'ghost'}
                    className="w-full justify-start rounded-lg"
                    onClick={() => setSettingsPanel('aiTemplates')}
                  >
                    {t('projectHub.settings.tabs.aiTemplates')}
                  </Button>
                  <Button
                    type="button"
                    variant={settingsPanel === 'aiTemplateBackup' ? 'default' : 'ghost'}
                    className="w-full justify-start rounded-lg"
                    onClick={() => setSettingsPanel('aiTemplateBackup')}
                  >
                    {t('projectHub.settings.tabs.aiBackup')}
                  </Button>
                </div>

                <div className="min-h-0 overflow-y-auto pr-1">
                  <div className="space-y-5 pb-1">
                  {settingsPanel === 'profile' ? (
                    <div className="space-y-4">
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-3 text-sm font-medium text-slate-800">{t('projectHub.settings.profile.title')}</div>
                        <div className="grid gap-2">
                          <Label htmlFor="profile-email" className="text-sm font-medium text-slate-700">
                            {t('auth.fields.email')}
                          </Label>
                          <Input
                            id="profile-email"
                            value={sessionEmail}
                            disabled
                            className="h-10 border-slate-200 bg-slate-100 text-sm text-slate-500"
                          />
                        </div>
                        <div className="mt-3 grid gap-2">
                          <Label htmlFor="profile-name" className="text-sm font-medium text-slate-700">
                            {t('projectHub.settings.profile.nickname')}
                          </Label>
                          <Input
                            id="profile-name"
                            value={profileName}
                            onChange={event => setProfileName(event.target.value)}
                            placeholder={t('projectHub.settings.profile.nicknamePlaceholder')}
                            className="h-10 border-slate-200 bg-white text-sm text-slate-800"
                          />
                        </div>
                        <div className="mt-4 flex justify-end">
                          <Button
                            type="button"
                            onClick={() => void handleSaveProfile()}
                            disabled={profileSaving || !profileName.trim()}
                          >
                            {profileSaving ? t('common.dialog.saving') : t('projectHub.settings.profile.saveNickname')}
                          </Button>
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-3 text-sm font-medium text-slate-800">{t('projectHub.settings.password.title')}</div>
                        <div className="grid gap-2">
                          <Label htmlFor="profile-current-password" className="text-sm font-medium text-slate-700">
                            {t('projectHub.settings.password.current')}
                          </Label>
                          <Input
                            id="profile-current-password"
                            type="password"
                            autoComplete="current-password"
                            value={currentPassword}
                            onChange={event => setCurrentPassword(event.target.value)}
                            placeholder={t('projectHub.settings.password.currentPlaceholder')}
                            className="h-10 border-slate-200 bg-white text-sm text-slate-800"
                          />
                        </div>
                        <div className="mt-3 grid gap-2">
                          <Label htmlFor="profile-new-password" className="text-sm font-medium text-slate-700">
                            {t('projectHub.settings.password.new')}
                          </Label>
                          <Input
                            id="profile-new-password"
                            type="password"
                            autoComplete="new-password"
                            value={newPassword}
                            onChange={event => setNewPassword(event.target.value)}
                            placeholder={t('projectHub.settings.password.newPlaceholder')}
                            className="h-10 border-slate-200 bg-white text-sm text-slate-800"
                          />
                        </div>
                        <div className="mt-3 grid gap-2">
                          <Label htmlFor="profile-confirm-password" className="text-sm font-medium text-slate-700">
                            {t('auth.fields.confirmPassword')}
                          </Label>
                          <Input
                            id="profile-confirm-password"
                            type="password"
                            autoComplete="new-password"
                            value={confirmNewPassword}
                            onChange={event => setConfirmNewPassword(event.target.value)}
                            placeholder={t('projectHub.settings.password.confirmPlaceholder')}
                            className="h-10 border-slate-200 bg-white text-sm text-slate-800"
                          />
                        </div>
                        <div className="mt-4 flex justify-end">
                          <Button
                            type="button"
                            onClick={() => void handleChangePassword()}
                            disabled={passwordChanging || !currentPassword || !newPassword || !confirmNewPassword}
                          >
                            {passwordChanging ? t('projectHub.settings.password.updating') : t('projectHub.settings.password.update')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : settingsPanel === 'aiTemplates' ? (
                    <div className="space-y-4">
                      <div className="text-sm font-medium text-slate-800">
                        {t('projectHub.settings.aiTemplates.title', { count: aiTemplates.length })}
                      </div>

                      <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 md:grid-cols-[minmax(0,1fr)_180px_180px]">
                        <div className="grid gap-1.5">
                          <Label className="text-xs font-medium text-slate-700">{t('projectHub.settings.aiTemplates.searchKeyword')}</Label>
                          <Input
                            value={aiTemplateKeyword}
                            onChange={event => setAiTemplateKeyword(event.target.value)}
                            placeholder={t('projectHub.settings.aiTemplates.searchPlaceholder')}
                            className="h-9 border-slate-200 bg-white text-sm text-slate-800"
                          />
                        </div>
                        <div className="grid gap-1.5">
                          <Label className="text-xs font-medium text-slate-700">{t('projectHub.settings.aiTemplates.project')}</Label>
                          <select
                            value={aiTemplateProjectFilter}
                            onChange={event => setAiTemplateProjectFilter(event.target.value)}
                            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800"
                          >
                            <option value="all">{t('projectHub.settings.aiTemplates.allProjects')}</option>
                            {projects.map(project => (
                              <option key={`ai-template-project-${project.id}`} value={project.id}>
                                {project.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="grid gap-1.5">
                          <Label className="text-xs font-medium text-slate-700">{t('projectHub.settings.aiTemplates.scope')}</Label>
                          <select
                            value={aiTemplateScopeFilter}
                            onChange={event => setAiTemplateScopeFilter(event.target.value as 'all' | TriageTemplateScope)}
                            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800"
                          >
                            <option value="all">{t('projectHub.settings.aiTemplates.allScopes')}</option>
                            <option value="builder">{t('ai.dialogs.import.context.tab1-builder')}</option>
                            <option value="results_header_builder">{t('ai.dialogs.import.context.tab3-results-header')}</option>
                            <option value="extractor">{t('ai.dialogs.import.context.tab2-extractor')}</option>
                          </select>
                        </div>
                      </div>

                      {aiTemplatesLoading ? (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-600">
                          {t('projectHub.settings.aiTemplates.loading')}
                        </div>
                      ) : aiTemplates.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-4 text-sm text-slate-500">
                          {t('projectHub.settings.aiTemplates.empty')}
                        </div>
                      ) : (
                        <div className="max-h-[380px] space-y-2 overflow-y-auto pr-1">
                          {aiTemplates.map(template => {
                            const projectTypeLabel = template.projectType === 'pdf' ? t('projectHub.settings.aiTemplates.projectTypePdf') : t('projectHub.settings.aiTemplates.projectTypePubmed');
                            const scopeLabel = formatTemplateScopeLabel(template.scope);
                            return (
                              <div key={template.id} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="truncate text-sm font-medium text-slate-900">
                                      {template.name?.trim() || template.firstFieldLabel || t('projectHub.settings.aiTemplates.unnamedTemplate')}
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                                      <Badge variant="outline">{scopeLabel}</Badge>
                                      <Badge variant="outline">{projectTypeLabel}</Badge>
                                      <span className="truncate">{template.projectName}</span>
                                      <span>·</span>
                                      <span>{new Date(template.updatedAt).toLocaleString()}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button type="button" variant="outline" size="sm" onClick={() => void handleOpenEditAiTemplate(template)}>
                                      {t('projectHub.settings.aiTemplates.edit')}
                                    </Button>
                                    <Button type="button" variant="outline" size="sm" onClick={() => handleOpenTemplateProject(template)}>
                                      {t('projectHub.settings.aiTemplates.openProject')}
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => handleRequestDeleteAiTemplate(template)}
                                      disabled={aiTemplateDeletingId === template.id}
                                    >
                                      {aiTemplateDeletingId === template.id ? t('projectHub.settings.aiTemplates.deleting') : t('projectHub.actions.delete')}
                                    </Button>
                                  </div>
                                </div>
                                <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
                                  <span className="font-medium">{t('projectHub.settings.aiTemplates.fieldLabel')}</span>{' '}
                                  {template.firstFieldLabel || '-'} ({template.firstFieldKey || '-'}) ·{' '}
                                  <span className="font-medium">{t('projectHub.settings.aiTemplates.modelLabel')}</span> {template.model}
                                </div>
                                <div className="truncate font-mono text-[11px] text-slate-400">{template.id}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {aiTemplateLimitReached ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                          {t('projectHub.settings.aiTemplates.limitHint')}
                        </div>
                      ) : null}
                    </div>
                  ) : settingsPanel === 'aiTemplateBackup' ? (
                    <div className="space-y-4">
                      <div className="text-sm font-medium text-slate-800">
                        {t('projectHub.settings.aiBackup.title')}
                      </div>

                      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="text-sm font-medium text-slate-800">{t('projectHub.settings.aiBackup.exportTitle')}</div>
                        <p className="text-xs text-slate-600">
                          {t('projectHub.settings.aiBackup.exportDescription')}
                        </p>
                        <div className="flex items-center justify-between gap-2">
                          <Button
                            type="button"
                            onClick={() => void handleExportAiTemplateBackup()}
                            disabled={aiBackupExporting || aiBackupImporting}
                          >
                            {aiBackupExporting ? t('projectHub.settings.aiBackup.exporting') : t('projectHub.settings.aiBackup.exportAction')}
                          </Button>
                          <span className="text-xs text-slate-500">
                            {aiBackupExporting ? aiBackupExportProgress || t('projectHub.settings.aiBackup.preparing') : ''}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="text-sm font-medium text-slate-800">{t('projectHub.settings.aiBackup.importTitle')}</div>
                        <p className="text-xs text-slate-600">
                          {t('projectHub.settings.aiBackup.importDescription')}
                        </p>
                        <div className="grid gap-2">
                          <Label htmlFor="ai-backup-file" className="text-xs font-medium text-slate-700">
                            {t('projectHub.settings.aiBackup.backupFile')}
                          </Label>
                          <Input
                            id="ai-backup-file"
                            type="file"
                            accept="application/json,.json"
                            onChange={event => handleSelectAiBackupImportFile(event.target.files?.[0] || null)}
                            disabled={aiBackupImporting || aiBackupExporting}
                            className="h-10 border-slate-200 bg-white text-sm text-slate-800 file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-2 file:py-1 file:text-xs file:font-medium file:text-slate-700"
                          />
                          <div className="text-xs text-slate-500">
                            {aiBackupImportFileName
                              ? t('projectHub.settings.aiBackup.selectedFile', { fileName: aiBackupImportFileName })
                              : t('projectHub.settings.aiBackup.noFileSelected')}
                          </div>
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="ai-backup-target-project" className="text-xs font-medium text-slate-700">
                            {t('projectHub.settings.aiBackup.missingProjectTarget')}
                          </Label>
                          <select
                            id="ai-backup-target-project"
                            value={aiBackupImportTargetProjectId}
                            onChange={event => setAiBackupImportTargetProjectId(event.target.value)}
                            disabled={aiBackupImporting || aiBackupExporting}
                            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800"
                          >
                            <option value={AI_BACKUP_IMPORT_TARGET_AUTO}>
                              {t('projectHub.settings.aiBackup.missingProjectTargetAuto')}
                            </option>
                            {projects.map(project => (
                              <option key={`ai-backup-target-${project.id}`} value={project.id}>
                                {project.name}
                              </option>
                            ))}
                          </select>
                          <div className="text-[11px] text-slate-500">
                            {t('projectHub.settings.aiBackup.missingProjectTargetHelp')}
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            onClick={() => void handleImportAiTemplateBackup()}
                            disabled={!aiBackupImportFileName || aiBackupImporting || aiBackupExporting}
                          >
                            {aiBackupImporting ? t('projectHub.settings.aiBackup.importing') : t('projectHub.settings.aiBackup.importAction')}
                          </Button>
                        </div>
                      </div>

                      {aiBackupLastReport ? (
                        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
                          <div className="text-sm font-medium text-slate-800">{t('projectHub.settings.aiBackup.lastImportReport')}</div>
                          <div className="grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                            <div>{t('projectHub.settings.aiBackup.report.file', { value: aiBackupLastReport.fileName })}</div>
                            <div>{t('projectHub.settings.aiBackup.report.total', { value: aiBackupLastReport.total })}</div>
                            <div className="text-emerald-700">{t('projectHub.settings.aiBackup.report.imported', { value: aiBackupLastReport.imported })}</div>
                            <div className="text-amber-700">{t('projectHub.settings.aiBackup.report.skippedDuplicate', { value: aiBackupLastReport.skippedDuplicate })}</div>
                            <div className="text-amber-700">{t('projectHub.settings.aiBackup.report.skippedMissingProject', { value: aiBackupLastReport.skippedMissingProject })}</div>
                            <div className="text-red-700">{t('projectHub.settings.aiBackup.report.failed', { value: aiBackupLastReport.failed })}</div>
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {t('projectHub.settings.aiBackup.report.startedFinished', {
                              startedAt: new Date(aiBackupLastReport.startedAt).toLocaleString(),
                              finishedAt: new Date(aiBackupLastReport.finishedAt).toLocaleString(),
                            })}
                          </div>

                          {aiBackupLastReport.entries.length > 0 ? (
                            <div className="space-y-2 border-t border-slate-100 pt-2">
                              {aiBackupLastReport.entries.map((entry, index) => (
                                <div key={`${entry.templateId || entry.templateName}-${index}`} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
                                  <div className="font-medium text-slate-900">
                                    [{formatBackupStatus(entry.status)}] {entry.templateName}
                                  </div>
                                  <div className="mt-0.5">{t('projectHub.settings.aiBackup.report.project', { value: entry.projectId || t('projectHub.settings.aiBackup.report.missingProject') })}</div>
                                  {entry.reason ? <div className="mt-0.5 text-slate-500">{entry.reason}</div> : null}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : settingsPanel === 'pubmedTemplates' || settingsPanel === 'templates' ? (
                    <div className="space-y-4">
                      <div className="text-sm font-medium text-slate-800">
                        {t('projectHub.settings.pubmedTemplates.title', { count: queryTemplates.length })}
                      </div>
                      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="text-sm font-medium text-slate-800">
                          {t('projectHub.settings.pubmedTemplates.createTitle')}
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="new-template-name" className="text-sm font-medium text-slate-700">
                            {t('projectHub.settings.pubmedTemplates.name')}
                          </Label>
                          <Input
                            id="new-template-name"
                            placeholder={t('projectHub.settings.pubmedTemplates.namePlaceholder')}
                            value={newTemplateName}
                            onChange={e => setNewTemplateName(e.target.value)}
                            className="h-10 border-slate-200 bg-white text-sm text-slate-800"
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="new-template-query" className="text-sm font-medium text-slate-700">
                            {t('projectHub.settings.pubmedTemplates.query')}
                          </Label>
                          <Textarea
                            id="new-template-query"
                            placeholder={t('projectHub.settings.pubmedTemplates.queryPlaceholder')}
                            value={newTemplateQuery}
                            onChange={e => setNewTemplateQuery(e.target.value)}
                            rows={3}
                            className="min-h-[88px] resize-y border-slate-200 bg-white text-sm text-slate-800"
                          />
                        </div>
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            onClick={handleCreateQueryTemplate}
                            disabled={queryTemplatesSaving || !newTemplateName.trim() || !newTemplateQuery.trim()}
                          >
                            {t('projectHub.settings.pubmedTemplates.createAction')}
                          </Button>
                        </div>
                      </div>

                      {queryTemplatesLoading ? (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-600">
                          {t('projectHub.settings.pubmedTemplates.loading')}
                        </div>
                      ) : queryTemplates.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-4 text-sm text-slate-500">
                          {t('projectHub.settings.pubmedTemplates.empty')}
                        </div>
                      ) : (
                        <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
                          {queryTemplates.map(template => {
                            const isEditing = editingQueryTemplateId === template.id;
                            const isDeleting = queryTemplatesDeletingId === template.id;
                            return (
                              <div key={template.id} className="rounded-lg border border-slate-200 bg-white p-3">
                                {isEditing ? (
                                  <div className="space-y-3">
                                    <div className="grid gap-2">
                                      <Label className="text-xs font-medium text-slate-600">{t('projectHub.settings.pubmedTemplates.name')}</Label>
                                      <Input
                                        value={editingQueryTemplateName}
                                        onChange={e => setEditingQueryTemplateName(e.target.value)}
                                        className="h-9 border-slate-200 bg-white text-sm text-slate-800"
                                      />
                                    </div>
                                    <div className="grid gap-2">
                                      <Label className="text-xs font-medium text-slate-600">{t('projectHub.settings.pubmedTemplates.query')}</Label>
                                      <Textarea
                                        value={editingQueryTemplateQuery}
                                        onChange={e => setEditingQueryTemplateQuery(e.target.value)}
                                        rows={3}
                                        className="min-h-[88px] resize-y border-slate-200 bg-white text-sm text-slate-800"
                                      />
                                    </div>
                                    <div className="flex items-center justify-end gap-2">
                                      <Button type="button" variant="outline" onClick={handleCancelEditQueryTemplate}>
                                        {t('common.dialog.cancel')}
                                      </Button>
                                      <Button
                                        type="button"
                                        onClick={() => void handleSaveEditQueryTemplate()}
                                        disabled={queryTemplatesSaving || !editingQueryTemplateName.trim() || !editingQueryTemplateQuery.trim()}
                                      >
                                        {t('common.dialog.save')}
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <div className="truncate text-sm font-medium text-slate-900">{template.name}</div>
                                        <div className="text-xs text-slate-500">
                                          {t('projectHub.settings.pubmedTemplates.updatedAt', { value: new Date(template.updatedAt).toLocaleString() })}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Button type="button" variant="outline" size="sm" onClick={() => handleStartEditQueryTemplate(template)}>
                                          {t('projectHub.settings.aiTemplates.edit')}
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="text-red-600 hover:text-red-700"
                                          onClick={() => void handleDeleteQueryTemplate(template)}
                                          disabled={isDeleting}
                                        >
                                          {isDeleting ? t('projectHub.settings.aiTemplates.deleting') : t('projectHub.actions.delete')}
                                        </Button>
                                      </div>
                                    </div>
                                    <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 font-mono text-xs text-slate-700">
                                      {template.query}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : settingsPanel === 'ocr' ? (
                    <>
                      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="text-sm font-medium text-slate-800">{t('projectHub.settings.ocr.title')}</div>
                            <p className="text-xs text-slate-500">
                              {t('projectHub.settings.ocr.description')}
                              {' '}
                              <span className="font-mono">https://api.siliconflow.cn/v1</span>
                            </p>
                          </div>
                          <Button type="button" variant="outline" size="sm" className="h-8 rounded-lg" onClick={handleAddOcrApiKey}>
                            <Plus className="mr-1 h-4 w-4" />
                            {t('projectHub.settings.ocr.addKey')}
                          </Button>
                        </div>

                        {settings.ocrApiKeys.length === 0 ? (
                          <div className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-500">
                            {t('projectHub.settings.ocr.empty')}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {settings.ocrApiKeys.map((key, index) => (
                              <div key={`ocr-key-${index}`} className="flex items-center gap-2">
                                <Input
                                  type="password"
                                  autoComplete="new-password"
                                  placeholder="sk-..."
                                  value={key}
                                  onChange={e => handleChangeOcrApiKey(index, e.target.value)}
                                  className="h-10 border-slate-200 bg-white text-sm text-slate-800"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-10 rounded-md"
                                  onClick={() => handleRemoveOcrApiKey(index)}
                                >
                                  {t('projectHub.settings.ocr.remove')}
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  ) : settingsPanel === 'ncbi' ? (
                    <>
                      <div className="grid gap-2">
                        <Label htmlFor="ncbi-api-key" className="text-sm font-medium text-slate-700">
                          {t('projectHub.settings.ncbi.apiKey')}
                        </Label>
                        <Input
                          id="ncbi-api-key"
                          type="password"
                          autoComplete="new-password"
                          placeholder={t('projectHub.settings.ncbi.apiKeyPlaceholder')}
                          value={settings.ncbiApiKey}
                          onChange={e => setSettings(prev => ({ ...prev, ncbiApiKey: e.target.value }))}
                          className="h-10 border-slate-200 bg-white text-sm text-slate-800"
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="ncbi-email" className="text-sm font-medium text-slate-700">
                          {t('projectHub.settings.ncbi.email')}
                        </Label>
                        <Input
                          id="ncbi-email"
                          placeholder={t('projectHub.settings.ncbi.emailPlaceholder')}
                          value={settings.ncbiEmail}
                          onChange={e => setSettings(prev => ({ ...prev, ncbiEmail: e.target.value }))}
                          className="h-10 border-slate-200 bg-white text-sm text-slate-800"
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="ncbi-tool" className="text-sm font-medium text-slate-700">
                          {t('projectHub.settings.ncbi.toolName')}
                        </Label>
                        <Input
                          id="ncbi-tool"
                          placeholder={t('projectHub.settings.ncbi.toolNamePlaceholder')}
                          value={settings.ncbiTool}
                          onChange={e => setSettings(prev => ({ ...prev, ncbiTool: e.target.value }))}
                          className="h-10 border-slate-200 bg-white text-sm text-slate-800"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-slate-800">
                          {t('projectHub.settings.pubmedTemplates.title', { count: queryTemplates.length })}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 rounded-lg"
                          onClick={() => void loadQueryTemplates()}
                          disabled={queryTemplatesLoading}
                        >
                          {queryTemplatesLoading ? t('projectHub.settings.pubmedTemplates.loading') : t('common.actions.refresh')}
                        </Button>
                      </div>

                      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="text-sm font-medium text-slate-800">{t('projectHub.settings.pubmedTemplates.createTitle')}</div>
                        <div className="grid gap-2">
                          <Label htmlFor="new-template-name" className="text-sm font-medium text-slate-700">
                            {t('projectHub.settings.pubmedTemplates.name')}
                          </Label>
                          <Input
                            id="new-template-name"
                            placeholder={t('projectHub.settings.pubmedTemplates.namePlaceholder')}
                            value={newTemplateName}
                            onChange={e => setNewTemplateName(e.target.value)}
                            className="h-10 border-slate-200 bg-white text-sm text-slate-800"
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="new-template-query" className="text-sm font-medium text-slate-700">
                            {t('projectHub.settings.pubmedTemplates.query')}
                          </Label>
                          <Textarea
                            id="new-template-query"
                            placeholder={t('projectHub.settings.pubmedTemplates.queryPlaceholder')}
                            value={newTemplateQuery}
                            onChange={e => setNewTemplateQuery(e.target.value)}
                            rows={3}
                            className="min-h-[88px] resize-y border-slate-200 bg-white text-sm text-slate-800"
                          />
                        </div>
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            onClick={handleCreateQueryTemplate}
                            disabled={queryTemplatesSaving || !newTemplateName.trim() || !newTemplateQuery.trim()}
                          >
                            {t('projectHub.settings.pubmedTemplates.createAction')}
                          </Button>
                        </div>
                      </div>

                      {queryTemplatesLoading ? (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-600">
                          {t('projectHub.settings.pubmedTemplates.loading')}
                        </div>
                      ) : queryTemplates.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-4 text-sm text-slate-500">
                          {t('projectHub.settings.pubmedTemplates.empty')}
                        </div>
                      ) : (
                        <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
                          {queryTemplates.map(template => {
                            const isEditing = editingQueryTemplateId === template.id;
                            const isDeleting = queryTemplatesDeletingId === template.id;
                            return (
                              <div key={template.id} className="rounded-lg border border-slate-200 bg-white p-3">
                                {isEditing ? (
                                  <div className="space-y-3">
                                    <div className="grid gap-2">
                                      <Label className="text-xs font-medium text-slate-600">{t('projectHub.settings.pubmedTemplates.name')}</Label>
                                      <Input
                                        value={editingQueryTemplateName}
                                        onChange={e => setEditingQueryTemplateName(e.target.value)}
                                        className="h-9 border-slate-200 bg-white text-sm text-slate-800"
                                      />
                                    </div>
                                    <div className="grid gap-2">
                                      <Label className="text-xs font-medium text-slate-600">{t('projectHub.settings.pubmedTemplates.query')}</Label>
                                      <Textarea
                                        value={editingQueryTemplateQuery}
                                        onChange={e => setEditingQueryTemplateQuery(e.target.value)}
                                        rows={3}
                                        className="min-h-[88px] resize-y border-slate-200 bg-white text-sm text-slate-800"
                                      />
                                    </div>
                                    <div className="flex items-center justify-end gap-2">
                                      <Button type="button" variant="outline" onClick={handleCancelEditQueryTemplate}>
                                        {t('common.dialog.cancel')}
                                      </Button>
                                      <Button
                                        type="button"
                                        onClick={() => void handleSaveEditQueryTemplate()}
                                        disabled={queryTemplatesSaving || !editingQueryTemplateName.trim() || !editingQueryTemplateQuery.trim()}
                                      >
                                        {t('common.dialog.save')}
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <div className="truncate text-sm font-medium text-slate-900">{template.name}</div>
                                        <div className="text-xs text-slate-500">
                                          {t('projectHub.settings.pubmedTemplates.updatedAt', { value: new Date(template.updatedAt).toLocaleString() })}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Button type="button" variant="outline" size="sm" onClick={() => handleStartEditQueryTemplate(template)}>
                                          {t('projectHub.settings.aiTemplates.edit')}
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="text-red-600 hover:text-red-700"
                                          onClick={() => void handleDeleteQueryTemplate(template)}
                                          disabled={isDeleting}
                                        >
                                          {isDeleting ? t('projectHub.settings.aiTemplates.deleting') : t('projectHub.actions.delete')}
                                        </Button>
                                      </div>
                                    </div>
                                    <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 font-mono text-xs text-slate-700">
                                      {template.query}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </CardContent>

            <CardFooter className="justify-end gap-2 border-t border-slate-100 pt-4">
              <Button
                variant="outline"
                onClick={onClose}
                className="border-primary/35 bg-white text-primary hover:bg-primary/10"
              >
                {t('projectHub.settings.close')}
              </Button>
            </CardFooter>
          </Card>
        </DialogContent>
      </Dialog>
  );
}

export default ProjectHubSettingsDialog;
